import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
    Plus, Pencil, Trash2, Power, PowerOff, Search,
    Upload, Download, Loader2, X, AlertTriangle, FileSpreadsheet,
    FileText, CheckCircle2, Eye, FileClock,
} from "lucide-react";
import { api } from "../../../lib/api";
import ImpactAnalysisModal from "./ImpactAnalysisModal";

/**
 * V53 — KindEditor (production-ready).
 * Handles CRUD for any master_data `kind`, including:
 *  - Search + parent-filter + include-inactive toggle
 *  - Pagination (200/page)
 *  - Soft delete with Impact-Analysis modal
 *  - Draft / Publish flag
 *  - CSV + XLSX bulk import (universal endpoint /admin/master/{kind}/bulk-import)
 *  - CSV + XLSX bulk export (server CSV download or client-side XLSX from current rows)
 *  - Version history popover via Audit panel
 */

const PARENT_OF = { state: "country", city: "state", pincode: "city" };

export default function KindEditor({ kind, label }) {
    const parentKind = PARENT_OF[kind] || null;

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [parents, setParents] = useState([]);
    const [parentId, setParentId] = useState("");
    const [includeInactive, setIncludeInactive] = useState(true);
    const [includeDrafts, setIncludeDrafts] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | item
    const [impactItem, setImpactItem] = useState(null);
    const [historyItem, setHistoryItem] = useState(null);

    const fileRef = useRef(null);

    // Parent options
    useEffect(() => {
        if (!parentKind) { setParents([]); setParentId(""); return; }
        api.get(`/admin/master/${parentKind}?limit=1000&include_inactive=false`)
            .then((r) => setParents(r.data.items || []))
            .catch(() => setParents([]));
    }, [parentKind]);

    useEffect(() => {
        if (parentKind && parents.length && !parentId) setParentId(parents[0].id);
    }, [parents, parentKind, parentId]);

    const load = useCallback(async () => {
        if (parentKind && !parentId) { setItems([]); setTotal(0); return; }
        setLoading(true);
        try {
            const qs = new URLSearchParams({ limit: "200", page: String(page) });
            if (search.trim()) qs.set("search", search.trim());
            if (parentKind && parentId) qs.set("parent_id", parentId);
            qs.set("include_inactive", String(includeInactive));
            qs.set("include_drafts", String(includeDrafts));
            const r = await api.get(`/admin/master/${kind}?${qs.toString()}`);
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load");
        } finally { setLoading(false); }
    }, [kind, parentKind, parentId, search, includeInactive, includeDrafts, page]);

    useEffect(() => { load(); }, [load]);

    // ── Actions
    const toggleActive = async (item) => {
        try {
            await api.patch(`/admin/master/${kind}/${item.id}`, { is_active: !item.is_active });
            toast.success(`${item.value} ${!item.is_active ? "activated" : "deactivated"}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const publishDraft = async (item) => {
        try {
            await api.post(`/admin/master/${kind}/${item.id}/publish`);
            toast.success(`"${item.value}" published`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const requestDelete = (item) => setImpactItem(item);

    const performDelete = async () => {
        const item = impactItem;
        if (!item) return;
        try {
            await api.delete(`/admin/master/${kind}/${item.id}`);
            toast.success("Moved to Recycle Bin");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete");
        } finally { setImpactItem(null); }
    };

    // ── Import: CSV or XLSX (auto-detect; backend handles both)
    const handleImport = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        const t = toast.loading(`Importing ${file.name}…`);
        try {
            const r = await api.post(`/admin/master/${kind}/bulk-import`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const d = r.data;
            toast.success(
                `Imported — created: ${d.created}, skipped: ${d.skipped}, errors: ${d.errors}`,
                { id: t },
            );
            if (d.errors && d.error_rows?.length) {
                console.warn("Bulk import errors:", d.error_rows);
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed", { id: t });
        } finally {
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    // ── Exports
    const exportCSV = async () => {
        try {
            const r = await api.get(`/admin/master/${kind}/export.csv`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `${kind}.csv`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Export failed"); }
    };

    const exportXLSX = async () => {
        // Backend xlsx export
        try {
            const r = await api.get(`/admin/master/${kind}/export.xlsx`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `${kind}.xlsx`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Export failed"); }
    };

    // ── Client-side XLSX preview (instant view of current page rows)
    const previewXLSX = () => {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(items.map((it) => ({
            id: it.id, value: it.value, code: it.code || "",
            parent_id: it.parent_id || "",
            is_active: it.is_active, is_draft: it.is_draft, sort_order: it.sort_order,
            extra_json: JSON.stringify(it.extra || {}),
        })));
        XLSX.utils.book_append_sheet(wb, ws, kind.slice(0, 31));
        XLSX.writeFile(wb, `${kind}-preview.xlsx`);
        toast.success("Preview XLSX downloaded (current view)");
    };

    const parentLabel = useMemo(() => parents.find((p) => p.id === parentId)?.value, [parents, parentId]);

    return (
        <div className="bg-white rounded-2xl border border-slate-200" data-testid={`md-kind-${kind}`}>
            {/* Toolbar */}
            <div className="px-3 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder={`Search ${label}…`}
                        data-testid={`md-${kind}-search`}
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    />
                </div>

                {parentKind && (
                    <select
                        value={parentId}
                        onChange={(e) => { setParentId(e.target.value); setPage(1); }}
                        data-testid={`md-${kind}-parent`}
                        className="text-sm px-3 py-2 rounded-lg border border-slate-200"
                    >
                        <option value="">— {parentKind} —</option>
                        {parents.map((p) => <option key={p.id} value={p.id}>{p.value}</option>)}
                    </select>
                )}

                <label className="inline-flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid={`md-${kind}-inactive`} />
                    Inactive
                </label>
                <label className="inline-flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={includeDrafts} onChange={(e) => setIncludeDrafts(e.target.checked)} data-testid={`md-${kind}-drafts`} />
                    Drafts
                </label>

                {/* Bulk ops */}
                <button onClick={() => fileRef.current?.click()} data-testid={`md-${kind}-import`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
                    <Upload className="w-3.5 h-3.5" /> Import CSV/XLSX
                </button>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleImport(e.target.files?.[0])} data-testid={`md-${kind}-import-input`} />
                <button onClick={exportCSV} data-testid={`md-${kind}-export-csv`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
                    <FileText className="w-3.5 h-3.5" /> CSV
                </button>
                <button onClick={exportXLSX} data-testid={`md-${kind}-export-xlsx`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
                    <FileSpreadsheet className="w-3.5 h-3.5" /> XLSX
                </button>

                <button onClick={() => setEditing("new")} data-testid={`md-${kind}-add`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                    <Plus className="w-3.5 h-3.5" /> Add
                </button>
            </div>

            {parentKind && !parentId && (
                <div className="px-4 py-6 text-sm text-slate-500 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> Select a {parentKind} above to view its {kind}s.
                </div>
            )}

            {/* List */}
            <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
                {loading && (
                    <div className="px-4 py-6 text-sm text-slate-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                )}
                {!loading && items.length === 0 && (!parentKind || parentId) && (
                    <div className="px-4 py-8 text-sm text-slate-500 text-center">
                        No {label} yet — click <strong>Add</strong> above, or use bulk import.
                    </div>
                )}
                {!loading && items.map((it) => (
                    <div key={it.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`md-row-${it.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`font-semibold ${it.is_active ? "text-slate-800" : "text-slate-400 line-through"}`}>{it.value}</span>
                                {it.code && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">{it.code}</span>}
                                {it.is_draft && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">DRAFT</span>}
                                {!it.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-semibold">INACTIVE</span>}
                                {it.version > 1 && <span className="text-[10px] text-slate-400">v{it.version}</span>}
                            </div>
                        </div>

                        {it.is_draft && (
                            <button onClick={() => publishDraft(it)} data-testid={`md-publish-${it.id}`} className="p-1.5 rounded hover:bg-amber-50" title="Publish">
                                <CheckCircle2 className="w-4 h-4 text-amber-600" />
                            </button>
                        )}
                        <button onClick={() => setHistoryItem(it)} data-testid={`md-history-${it.id}`} className="p-1.5 rounded hover:bg-slate-100" title="Version history">
                            <FileClock className="w-4 h-4 text-slate-500" />
                        </button>
                        <button onClick={() => toggleActive(it)} data-testid={`md-toggle-${it.id}`} className="p-1.5 rounded hover:bg-slate-100" title={it.is_active ? "Deactivate" : "Activate"}>
                            {it.is_active ? <PowerOff className="w-4 h-4 text-slate-500" /> : <Power className="w-4 h-4 text-emerald-600" />}
                        </button>
                        <button onClick={() => setEditing(it)} data-testid={`md-edit-${it.id}`} className="p-1.5 rounded hover:bg-slate-100" title="Edit">
                            <Pencil className="w-4 h-4 text-slate-600" />
                        </button>
                        <button onClick={() => requestDelete(it)} data-testid={`md-delete-${it.id}`} className="p-1.5 rounded hover:bg-red-50" title="Soft delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Footer / pagination */}
            <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between flex-wrap gap-2">
                <span>
                    {loading ? "…" : `${items.length} shown · ${total} total${parentLabel ? ` under "${parentLabel}"` : ""}`}
                </span>
                <span className="inline-flex items-center gap-2">
                    <button onClick={previewXLSX} data-testid={`md-${kind}-preview-xlsx`} className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">
                        <Eye className="w-3 h-3" /> Preview as XLSX
                    </button>
                    {total > items.length && (
                        <span className="inline-flex items-center gap-1">
                            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-50">Prev</button>
                            <span>Page {page}</span>
                            <button disabled={items.length < 200} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-50">Next</button>
                        </span>
                    )}
                </span>
            </div>

            {editing && (
                <ItemEditor
                    kind={kind}
                    item={editing === "new" ? null : editing}
                    parentKind={parentKind}
                    parents={parents}
                    defaultParentId={parentId}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}

            {impactItem && (
                <ImpactAnalysisModal
                    kind={kind}
                    item={impactItem}
                    onClose={() => setImpactItem(null)}
                    onConfirm={performDelete}
                />
            )}

            {historyItem && (
                <HistoryModal kind={kind} item={historyItem} onClose={() => setHistoryItem(null)} onRolledBack={() => { setHistoryItem(null); load(); }} />
            )}
        </div>
    );
}

function ItemEditor({ kind, item, parentKind, parents, defaultParentId, onClose, onSaved }) {
    const [value, setValue] = useState(item?.value || "");
    const [code, setCode] = useState(item?.code || "");
    const [sortOrder, setSortOrder] = useState(item?.sort_order ?? 100);
    const [parentId, setParentId] = useState(item?.parent_id || defaultParentId || "");
    const [isPrimary, setIsPrimary] = useState(Boolean(item?.extra?.is_primary));
    const [active, setActive] = useState(item?.is_active ?? true);
    const [isDraft, setIsDraft] = useState(item?.is_draft ?? false);
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!value.trim()) { toast.error("Value is required"); return; }
        if (parentKind && !parentId) { toast.error(`Please choose a ${parentKind}`); return; }
        setSaving(true);
        try {
            const payload = {
                value: value.trim(),
                code: code.trim() || null,
                sort_order: Number(sortOrder) || 100,
                is_active: active,
                is_draft: isDraft,
            };
            if (parentKind) payload.parent_id = parentId;
            if (kind === "grade") payload.extra = { is_primary: isPrimary };
            if (item) {
                await api.patch(`/admin/master/${kind}/${item.id}`, payload);
                toast.success("Updated");
            } else {
                await api.post(`/admin/master/${kind}`, payload);
                toast.success("Created");
            }
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3" data-testid="md-editor-modal">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-bold text-[#0F172A]">{item ? "Edit" : "Add"} {kind}</h3>
                    <button type="button" onClick={onClose} data-testid="md-editor-close" className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
                </div>

                <Field label="Value *">
                    <input value={value} onChange={(e) => setValue(e.target.value)} autoFocus data-testid="md-editor-value" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
                </Field>

                {parentKind && (
                    <Field label={`Parent ${parentKind} *`}>
                        <select value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="md-editor-parent" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                            <option value="">— select —</option>
                            {parents.map((p) => <option key={p.id} value={p.id}>{p.value}</option>)}
                        </select>
                    </Field>
                )}

                {(kind === "country" || kind === "subject" || kind === "board" || kind === "curriculum_pack") && (
                    <Field label="Short code (optional)">
                        <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="md-editor-code" placeholder={kind === "country" ? "IN" : "MATH"} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                    </Field>
                )}

                <div className="grid grid-cols-3 gap-2">
                    <Field label="Sort order">
                        <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} data-testid="md-editor-sort" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                    </Field>
                    <Field label="Status">
                        <label className="inline-flex items-center gap-2 text-sm pt-2">
                            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="md-editor-active" />
                            Active
                        </label>
                    </Field>
                    <Field label="Draft">
                        <label className="inline-flex items-center gap-2 text-sm pt-2">
                            <input type="checkbox" checked={isDraft} onChange={(e) => setIsDraft(e.target.checked)} data-testid="md-editor-draft" />
                            Save as draft
                        </label>
                    </Field>
                </div>

                {kind === "grade" && (
                    <label className="flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 cursor-pointer">
                        <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} data-testid="md-editor-is-primary" className="mt-0.5" />
                        <span><strong>Primary-level grade.</strong> Eligible for &quot;All Major Subjects&quot; shortcut in AI matching.</span>
                    </label>
                )}

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button type="submit" disabled={saving} data-testid="md-editor-save" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save
                    </button>
                </div>
            </form>
        </div>
    );
}

function HistoryModal({ kind, item, onClose, onRolledBack }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/admin/master/${kind}/${item.id}/history`)
            .then((r) => setRows(r.data.items || []))
            .finally(() => setLoading(false));
    }, [kind, item.id]);

    const rollback = async (audit) => {
        if (!window.confirm(`Rollback "${item.value}" to the state before this change?`)) return;
        try {
            await api.post(`/admin/master/${kind}/${item.id}/rollback`, { audit_id: audit.id });
            toast.success("Rolled back");
            onRolledBack?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rollback failed");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-xl w-full p-5 space-y-3" data-testid="md-history-modal">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-bold text-[#0F172A]">Version history — {item.value}</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
                </div>
                {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
                {!loading && rows.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No history entries.</div>}
                <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100">
                    {rows.map((a) => {
                        const diff = a.diff || {};
                        const keys = Object.keys(diff).filter((k) => k !== "_op");
                        return (
                            <div key={a.id} className="py-2 text-xs" data-testid={`md-hist-row-${a.id}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{a.op}</span>
                                    <span className="text-slate-500">{new Date(a.at).toLocaleString()}</span>
                                    <span className="text-slate-400">·</span>
                                    <span className="text-slate-600 truncate">{a.actor_email}</span>
                                    {a.op === "update" && keys.length > 0 && (
                                        <button onClick={() => rollback(a)} data-testid={`md-hist-rollback-${a.id}`} className="ml-auto text-[10px] px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-bold hover:bg-orange-200">
                                            ↶ Rollback
                                        </button>
                                    )}
                                </div>
                                {keys.length > 0 && (
                                    <div className="mt-1 ml-1 text-[11px] text-slate-600 space-y-0.5">
                                        {keys.map((k) => {
                                            const v = diff[k];
                                            if (typeof v === "object" && v && "before" in v) {
                                                return <div key={k}><span className="font-semibold">{k}:</span> <span className="line-through text-rose-600">{JSON.stringify(v.before)}</span> → <span className="text-emerald-700">{JSON.stringify(v.after)}</span></div>;
                                            }
                                            return null;
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</span>
            {children}
        </label>
    );
}
